import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  baseHue: number;
  life: number;
}

@Component({
  selector: 'app-innovation-particle-canvas',
  template: '<canvas #canvas (mousemove)="onMove($event)" (mouseleave)="onLeave()" (click)="onClick($event)"></canvas>',
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
        cursor: none;
      }
      canvas {
        width: 100%;
        height: 100%;
        display: block;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InnovationParticleCanvasComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @Input() particleCount = 1200;
  @Input() trailOpacity = 0.06;

  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private ngZone = inject(NgZone);
  private hostEl = inject<ElementRef<HTMLElement>>(ElementRef);

  private ctx!: CanvasRenderingContext2D;
  private w = 0;
  private h = 0;
  private dpr = 1;
  private animationId = 0;
  private resizeObserver!: ResizeObserver;
  private intersectionObserver!: IntersectionObserver;
  private isVisible = true;
  private particles: Particle[] = [];
  private time = 0;

  private mouseX = -9999;
  private mouseY = -9999;
  private mouseActive = false;
  private mouseInfluence = 0;

  private bursts: { x: number; y: number; strength: number; age: number }[] = [];

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.initCanvas();
      this.createParticles();
      this.animate();
      this.observeResize();
      this.observeVisibility();
    });
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) {
      return;
    }
    cancelAnimationFrame(this.animationId);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
  }

  onMove(e: MouseEvent): void {
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    this.mouseX = (e.clientX - rect.left) * this.dpr;
    this.mouseY = (e.clientY - rect.top) * this.dpr;
    this.mouseActive = true;
  }

  onLeave(): void {
    this.mouseActive = false;
  }

  onClick(e: MouseEvent): void {
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    this.bursts.push({
      x: (e.clientX - rect.left) * this.dpr,
      y: (e.clientY - rect.top) * this.dpr,
      strength: 1,
      age: 0,
    });
  }

  private initCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d')!;
    this.dpr = Math.min(window.devicePixelRatio, 2);
    this.resize();
  }

  private resize(): void {
    const canvas = this.canvasRef.nativeElement;
    const rect = this.hostEl.nativeElement.getBoundingClientRect();
    this.w = rect.width * this.dpr;
    this.h = rect.height * this.dpr;
    canvas.width = this.w;
    canvas.height = this.h;
  }

  private createParticles(): void {
    this.particles = [];
    for (let i = 0; i < this.particleCount; i++) {
      this.particles.push({
        x: Math.random() * this.w,
        y: Math.random() * this.h,
        vx: 0,
        vy: 0,
        size: 0.5 + Math.random() * 2,
        baseHue: 260 + Math.random() * 40, // purple range 260-300
        life: Math.random(),
      });
    }
  }

  // Multi-octave noise approximation
  private flowAngle(x: number, y: number, t: number): number {
    const s1 = Math.sin(x * 0.003 + t * 0.4) * Math.cos(y * 0.004 + t * 0.3);
    const s2 = Math.sin(x * 0.007 - t * 0.25) * Math.cos(y * 0.006 + t * 0.15) * 0.5;
    const s3 = Math.sin(x * 0.013 + t * 0.6) * Math.cos(y * 0.011 - t * 0.2) * 0.25;

    return (s1 + s2 + s3) * Math.PI * 2;
  }

  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate);
    if (!this.isVisible) {
      return;
    }
    this.time += 0.008;

    // Fade trails
    this.ctx.fillStyle = `rgba(245, 242, 250, ${this.trailOpacity})`;
    this.ctx.fillRect(0, 0, this.w, this.h);

    // Mouse influence easing
    if (this.mouseActive) {
      this.mouseInfluence += (1 - this.mouseInfluence) * 0.08;
    } else {
      this.mouseInfluence *= 0.96;
    }

    // Update bursts
    for (let i = this.bursts.length - 1; i >= 0; i--) {
      this.bursts[i].age += 0.02;
      this.bursts[i].strength *= 0.95;
      if (this.bursts[i].strength < 0.01) {
        this.bursts.splice(i, 1);
      }
    }

    // Update and draw particles
    for (const p of this.particles) {
      // Flow field force
      const angle = this.flowAngle(p.x, p.y, this.time);
      const flowStrength = 0.3;
      p.vx += Math.cos(angle) * flowStrength;
      p.vy += Math.sin(angle) * flowStrength;

      // Mouse vortex
      if (this.mouseInfluence > 0.01) {
        const dx = this.mouseX - p.x;
        const dy = this.mouseY - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const radius = 180 * this.dpr;

        if (dist < radius && dist > 1) {
          const force = (1 - dist / radius) * this.mouseInfluence;
          // Attraction toward cursor
          const attract = force * 0.8;
          p.vx += (dx / dist) * attract;
          p.vy += (dy / dist) * attract;
          // Tangential swirl
          const swirl = force * 1.5;
          p.vx += (-dy / dist) * swirl;
          p.vy += (dx / dist) * swirl;
        }
      }

      // Burst repulsion
      for (const burst of this.bursts) {
        const dx = p.x - burst.x;
        const dy = p.y - burst.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const burstRadius = 250 * this.dpr * burst.age;

        if (dist < burstRadius && dist > 1) {
          const force = burst.strength * (1 - dist / burstRadius) * 8;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }
      }

      // Damping
      p.vx *= 0.92;
      p.vy *= 0.92;

      // Update position
      p.x += p.vx;
      p.y += p.vy;

      // Wrap boundaries
      if (p.x < -10) p.x = this.w + 10;
      if (p.x > this.w + 10) p.x = -10;
      if (p.y < -10) p.y = this.h + 10;
      if (p.y > this.h + 10) p.y = -10;

      // Draw
      const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      const alpha = Math.min(0.15 + speed * 0.12, 0.85);
      const lightness = 45 + speed * 5;
      const saturation = 50 + speed * 8;
      const size = p.size * (1 + speed * 0.15);

      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
      this.ctx.fillStyle = `hsla(${p.baseHue}, ${saturation}%, ${lightness}%, ${alpha})`;
      this.ctx.fill();
    }

    // Draw cursor glow
    if (this.mouseInfluence > 0.05) {
      const gradient = this.ctx.createRadialGradient(
        this.mouseX,
        this.mouseY,
        0,
        this.mouseX,
        this.mouseY,
        100 * this.dpr * this.mouseInfluence,
      );
      gradient.addColorStop(0, `rgba(141, 97, 225, ${0.08 * this.mouseInfluence})`);
      gradient.addColorStop(1, 'rgba(141, 97, 225, 0)');
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, this.w, this.h);
    }
  };

  private observeResize(): void {
    this.resizeObserver = new ResizeObserver(() => {
      const oldW = this.w;
      const oldH = this.h;
      this.resize();
      // Rescale particle positions
      if (oldW > 0 && oldH > 0) {
        const scaleX = this.w / oldW;
        const scaleY = this.h / oldH;
        for (const p of this.particles) {
          p.x *= scaleX;
          p.y *= scaleY;
        }
      }
    });
    this.resizeObserver.observe(this.hostEl.nativeElement);
  }

  private observeVisibility(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.isVisible = entries[0].isIntersecting;
      },
      { threshold: 0 },
    );
    this.intersectionObserver.observe(this.hostEl.nativeElement);
  }
}
